import { useState } from "react";
import { Form, required, useLogin, useNotify } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link } from "react-router";
import {
  CheckCircle2,
  PhoneCall,
  MessageSquare,
  FileText,
  Receipt,
  Briefcase,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { SSOAuthButton } from "./SSOAuthButton";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { darkModeLogo, title, googleWorkplaceDomain } =
    useConfigurationContext();
  const { redirectTo } = props;

  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error?.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        {/* LEFT PANEL (brand + value) */}
        <div className="relative hidden h-full flex-col p-10 text-white dark:border-r lg:flex overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-zinc-950" />
          <div className="absolute inset-0 opacity-60 bg-[radial-gradient(ellipse_at_top_left,rgba(59,130,246,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(34,197,94,0.18),transparent_55%)]" />
          <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />

          <div className="relative z-20 flex items-center gap-3">
            <img className="h-7 w-7" src={darkModeLogo} alt={title} />
            {/* Remove duplicate title look: keep title, add “OS” descriptor */}
            <div className="flex flex-col leading-tight">
              <div className="text-lg font-semibold tracking-tight">{title}</div>
              <div className="text-xs text-white/70">
                The operating system for contractors
              </div>
            </div>
          </div>

          <div className="relative z-20 mt-10 max-w-md">
            <h2 className="text-3xl font-semibold tracking-tight">
              Run your business like a{" "}
              <span className="text-white">real operator</span>.
            </h2>
            <p className="mt-3 text-sm text-white/75 leading-relaxed">
              Calls, texts, leads, estimates, jobs, invoices, vendors, subs —
              connected end-to-end with a defensible audit trail.
            </p>

            <div className="mt-7 grid gap-3">
              <Feature
                icon={PhoneCall}
                title="Calls → leads automatically"
                desc="Every inbound call becomes a trackable lead with timeline events."
              />
              <Feature
                icon={MessageSquare}
                title="SMS threads per contact"
                desc="Chronological messaging with delivery status + activity log."
              />
              <Feature
                icon={FileText}
                title="Estimates that close"
                desc="Line items, delivery status, accept/decline, and conversion tracking."
              />
              <Feature
                icon={Briefcase}
                title="Jobs + job costing"
                desc="Accepted estimates become jobs with expenses and margin visibility."
              />
              <Feature
                icon={Receipt}
                title="Invoices + payments"
                desc="Deposit/progress/final invoices with payment workflows."
              />
              <Feature
                icon={ShieldCheck}
                title="Enterprise-grade accountability"
                desc="Immutable events + audit log designed for disputes and compliance."
              />
            </div>

            <div className="mt-8 flex items-center gap-2 text-xs text-white/70">
              <CheckCircle2 className="size-4 text-emerald-300" />
              <span>
                Built for small operators through multi-crew GCs — without the
                complexity.
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL (form) */}
        <div className="flex flex-col justify-center w-full p-4 lg:p-10 bg-background">
          {/* Mobile header brand (since left panel is hidden) */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img className="h-7 w-7" src={darkModeLogo} alt={title} />
            <div className="flex flex-col leading-tight">
              <div className="text-base font-semibold">{title}</div>
              <div className="text-xs text-muted-foreground">
                Sign in to continue
              </div>
            </div>
          </div>

          <div className="w-full space-y-6 lg:mx-auto lg:w-[380px]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Use your email and password to access your workspace.
              </p>
            </div>

            <Form className="space-y-6" onSubmit={handleSubmit}>
              <TextInput
                label="Email"
                source="email"
                type="email"
                validate={required()}
              />
              <TextInput
                label="Password"
                source="password"
                type="password"
                validate={required()}
              />

              <div className="flex flex-col gap-3">
                <Button
                  type="submit"
                  className="w-full cursor-pointer"
                  disabled={loading}
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>

                {googleWorkplaceDomain ? (
                  <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                    Sign in with Google Workspace
                  </SSOAuthButton>
                ) : null}
              </div>
            </Form>

            <div className="text-center">
              <Link
                to={"/forgot-password"}
                className="inline-block text-sm text-muted-foreground hover:text-foreground hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Notification />
    </div>
  );
};

function Feature(props: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}) {
  const Icon = props.icon;
  return (
    <div className="flex gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
        <Icon className="size-4 text-white/90" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white/95">{props.title}</div>
        <div className="text-xs text-white/70 leading-relaxed">{props.desc}</div>
      </div>
    </div>
  );
}